import earlyRepaymentCalculator from './earlyRepaymentCalculator';
import selfServeReOfferModal from './selfServeReOfferModal';

import React from 'react';
import { Link, Paragraph } from 'operation-diameter';

export default {
  earlyRepaymentCalculator,
  selfServeReOfferModal,
  title: {
    default: 'We’ve got a great offer for you!',
    stl: {
      withIntent: 'We have a personalised offer for you!',
      downsell: 'We have an offer for you!'
    }
  },
  subtitle: {
    fixedRate:
      'This offer comes with fixed rates, so you can plan with confidence.',
    creditBuilder: {
      offer:
        'We’re delighted to offer you our short-term loan which comes with no upfront fee.',
      fee: 'There’s also no fee for full early repayment, so if you choose to settle early, you’ll only pay the interest on the time you borrowed.'
    },
    stl: {
      withIntent: () => (
        <>
          After reviewing your application, we’ve offered you the highest amount
          possible based on our affordability assessment. If you’d like to
          discuss adjustments to your offer, give us a call at{' '}
          <Link href='tel:0800 048 2467'>0800 048 2467</Link> or email us at{' '}
          <Link href='mailto:credit@fundingcircle.com'>
            credit@fundingcircle.com
          </Link>
          . Your Account Manager will be happy to help you.
        </>
      ),
      downsell: () => (
        <>
          After reviewing your application, we’ve offered you the highest amount
          and longest term possible based on our affordability assessment. If
          you’d like to discuss adjustments to your offer, give us a call at{' '}
          <Link href='tel:0800 048 2467'>0800 048 2467</Link> or email us at{' '}
          <Link href='mailto:credit@fundingcircle.com'>
            credit@fundingcircle.com
          </Link>
          . Your Account Manager will be happy to help you.
        </>
      ),
      term: ({ termMonths }) => (
        <>
          The loan term is up to {termMonths} months, but with the freedom to
          repay any amount at any time so you stay in control of your finances.
          The sooner you pay back, the more you save.
        </>
      )
    },
    ggs: 'Good news - you can get a loan under the Growth Guarantee Scheme.'
  },
  offerDetails: {
    default: {
      amount: 'Offer amount',
      monthlyRepayment: 'Monthly repayment',
      interestRate: 'Interest rate',
      term: 'Term',
      adjustOffer: 'Adjust your offer'
    },
    refinance: {
      amount: 'Loan amount',
      settlementAmount: 'Current balance to be settled*',
      receivedAmount: "You'll receive",
      totalRepayment: 'Total repayment'
    },
    shortTermLoan: {
      amount: 'Offer amount',
      monthlyRepayment: 'Monthly repayment',
      repaymentStatement: (term) => <>(if repaid over {term} months)</>,
      interestRate: 'Interest rate',
      originationFee: 'Completion fee',
      pill: 'You requested'
    },
    breakdown: {
      viewFull: 'Total repayment if you settle in full',
      button: 'View full breakdown',
      saving: ({ saving }) => <>Save {saving}</>,
      modal: {
        heading: 'Your total repayments',
        firstParagraph:
          'You can overpay or settle early at any time with no penalty fees. The sooner you repay, the more you’ll save.',
        secondParagraph: ({ totalAmountRepayable, termMonths }) => (
          <>
            You’ll only pay the full amount of {totalAmountRepayable} if you
            borrow for the maximum {termMonths} months.
          </>
        ),
        columns: {
          months: 'Month',
          repayments: 'Early settlement amount',
          savings: 'Amount saved'
        }
      }
    }
  },
  offerFeatures: {
    default: () => [
      'Overpay, or settle early with no penalty fees',
      'Interest only accrues on the amount left to pay',
      'Dedicated Account Manager',
      'Funds paid out typically within 48 hours'
    ]
  },
  faq: {
    variants: {
      default: () => ['canITalkToSomeone'],
      stl: () => ['howDoOverpaymentsWork', 'howDoIDiscussWithAM'],
      stlDowngrade: () => [
        'whyHaveIReceivedStlDowngrade',
        'howDoOverpaymentsWork',
        'howDoIDiscussWithAM'
      ]
    },
    items: {
      canITalkToSomeone: {
        title: 'Can I talk to someone about my offer?',
        description: () => (
          <Paragraph>
            We're here to help if you need us. Your dedicated Account Manager is
            available to answer any questions you may have. You can call us on{' '}
            <Link href='tel:08000482467'>0800 048 2467</Link>
            <span>&nbsp;</span>or email us at{' '}
            <Link href='mailto:credit@fundingcircle.com'>
              credit@fundingcircle.com
            </Link>
            <span>&nbsp;</span>
          </Paragraph>
        )
      },
      whyHaveIReceivedStlDowngrade: {
        title: 'Why have I received this offer?',
        description: () => (
          <>
            <Paragraph>
              After reviewing your application, we’ve provided the highest
              amount possible based on our affordability assessment. If you’d
              like to discuss adjustments to your offer, feel free to reach out.
            </Paragraph>
            <Paragraph>
              With the freedom to repay any amount at any time, you stay in
              control of your finances. The sooner you pay back, the more you
              save.
            </Paragraph>
          </>
        )
      },
      howDoOverpaymentsWork: {
        title: 'How do overpayments work?',
        description: () => (
          <>
            <Paragraph>
              Overpayments allow you to pay more than your contractual monthly
              amount. With a Funding Circle short-term business loan, interest
              accrues only on the remaining balance. So, if you overpay, you can
              shorten your loan term and save by reducing the amount of interest
              you pay.
            </Paragraph>
            <Paragraph>
              Please note that you’ll still need to cover your contractual
              monthly repayment, along with any overpayments you choose to make.
            </Paragraph>
          </>
        )
      },
      howDoIDiscussWithAM: {
        title: 'How do I discuss my options with an Account Manager?',
        description: () => (
          <Paragraph>
            We're here to help if you need us. Your dedicated Account Manager is
            available to answer any questions you might have. You can call us on{' '}
            <Link href='tel:08000482467'>0800 048 2467</Link>
            <span>&nbsp;</span>or email us at{' '}
            <Link href='mailto:credit@fundingcircle.com'>
              credit@fundingcircle.com
            </Link>
            <span>&nbsp;</span>
          </Paragraph>
        )
      }
    }
  },
  companionOfferBanner: {
    title: (creditLimit) =>
      `You’re also pre-approved for our Cashback Credit Card with a £${creditLimit} credit limit`,
    description: `You can choose to set this up after finalising your loan or you can accept your offer at any time within the next 30 days in your account. Speak to your account manager to find out more.`,
    notice: `No additional credit checks have been run to provide this offer.`
  },
  acceptOffer: {
    button: 'Proceed with offer',
    eligibleForReOffer: () => (
      <>
        <b>Want to change the terms of your offer?</b> One of our dedicated
        Account Managers will be in contact very shortly to discuss your offer,
        and they can help you make changes before you proceed.
      </>
    ),
    subjectToChecks: 'This offer is subject to a few further checks from us.',
    notGgs:
      'It is not a loan under the Government Growth Guarantee Lending Scheme.',
    refinance:
      '*This value may not be reflective of any payments you have made in the last few days.',
    annualInterestRate: ({ annualInterestRate }) => (
      <>
        Your monthly rate is the equivalent to an annual rate of{' '}
        {annualInterestRate}%.
      </>
    )
  }
};
